import { useCallback, useEffect, useState } from 'react'

import {
  ApiError,
  type DirectoryEntry,
  type FaxRouting as FaxRoutingData,
  type Recipient,
  type RecipientInput,
  getFaxRouting,
  saveFaxRouting,
  searchDirectory,
} from '../lib/api'

// The "Send to Fax" fieldset of Study Information. Data capture only.
//
// `StudyInformation.aspx:107` has this as a `<fieldset><LEGEND>Send to Fax`
// inside the study page, with nested fieldsets per recipient — it was never a
// page of its own. It is a section component for that reason, rendered by
// `StudyInformation.tsx` rather than routed.
//
//
// There is no Send button and there must not be one: transmission is out of
// scope (DEC-013). This screen collects recipients and numbers and saves them
// to the twelve live columns on `Studies`. Provisional under DEC-015.
//
// Three things here have no legacy equivalent, each targeting a measured defect:
//   * drift hints — 31.6% of stored numbers disagree with the directory
//   * placeholder warnings — 12.3% of stored values are junk like 0000000000
//   * the invariant check — 26 studies are flagged to fax with no number

type Draft = Record<string, RecipientInput>

function draftFrom(data: FaxRoutingData): Draft {
  const d: Draft = {}
  for (const r of data.recipients) {
    d[r.slot] = {
      recipient_id: r.recipient_id,
      // Show the raw stored value so malformed data is visible and fixable,
      // rather than silently hidden behind a formatter.
      fax: r.fax_stored ?? '',
      include: r.include,
    }
  }
  return d
}

export default function FaxRoutingSection({ studyId }: { studyId: number }) {
  const id = studyId

  const [data, setData] = useState<FaxRoutingData | null>(null)
  const [draft, setDraft] = useState<Draft>({})
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      const d = await getFaxRouting(id)
      setData(d)
      setDraft(draftFrom(d))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    }
  }, [id])

  useEffect(() => {
    if (Number.isFinite(id)) void load()
  }, [id, load])

  function update(slot: string, patch: Partial<RecipientInput>) {
    setDraft((d) => ({ ...d, [slot]: { ...d[slot], ...patch } }))
    setNotice(null)
  }

  async function onSave() {
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      const res = await saveFaxRouting(id, draft)
      setNotice(res.message)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  if (!Number.isFinite(id)) return null
  if (error && !data)
    return (
      <fieldset className="ro-group">
        <legend>Send to Fax</legend>
        <p className="banner banner-error">{error}</p>
      </fieldset>
    )
  if (!data)
    return (
      <fieldset className="ro-group">
        <legend>Send to Fax</legend>
        <p className="muted">Loading…</p>
      </fieldset>
    )

  return (
    <fieldset className="ro-group">
      <legend>Send to Fax</legend>

      {!data.writable && (
        <p className="banner banner-warn">
          <strong>Read-only.</strong> {data.read_only_reason}
        </p>
      )}
      {error && <p className="banner banner-error">{error}</p>}
      {notice && <p className="banner banner-ok">{notice}</p>}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void onSave()
        }}
      >
        {data.recipients.map((r) => (
          <RecipientRow
            key={r.slot}
            recipient={r}
            value={draft[r.slot]}
            disabled={!data.writable}
            onChange={(patch) => update(r.slot, patch)}
          />
        ))}

        <div className="actions">
          <button type="submit" disabled={!data.writable || saving}>
            {/* Not plain "Save": this section is rendered inside Study
                Information, whose assignment form has its own Save. Two
                buttons with the identical accessible name cannot be told
                apart by a screen reader, or by anything else. */}
            {saving ? 'Saving…' : 'Save fax routing'}
          </button>
          {/* Deliberately no Send button — see DEC-013. */}
          <span className="subtle">
            Saves recipients and numbers. Does not send anything.
          </span>
        </div>
      </form>

      {data.standing_destinations.length > 0 && (
        <section className="standing">
          <h2>Standing destinations</h2>
          <p className="subtle">
            Routing rules hard-coded in the legacy application, shown for
            reference. Not editable here, and not reproduced as behaviour.
          </p>
          <table>
            <thead>
              <tr>
                <th>Destination</th>
                <th>Fax</th>
                <th>Applies when</th>
              </tr>
            </thead>
            <tbody>
              {data.standing_destinations.map((s) => (
                <tr key={s.name} className={s.disputed ? 'row-disputed' : undefined}>
                  <td>{s.name}</td>
                  <td>
                    {s.fax_display}
                    {s.disputed && <span className="tag">unconfirmed</span>}
                  </td>
                  <td className="subtle">{s.trigger}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </fieldset>
  )
}

function RecipientRow({
  recipient,
  value,
  disabled,
  onChange,
}: {
  recipient: Recipient
  value: RecipientInput | undefined
  disabled: boolean
  onChange: (patch: Partial<RecipientInput>) => void
}) {
  const v = value ?? { recipient_id: null, fax: '', include: false }
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<DirectoryEntry[]>([])
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const t = setTimeout(async () => {
      try {
        const r = await searchDirectory(query)
        if (!cancelled) setResults(r)
      } catch {
        if (!cancelled) setResults([])
      }
    }, 200)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [query, open])

  // Snapshot semantics, deliberately: selecting a recipient copies the
  // directory's number into the field, and the copy is what gets stored. This
  // is how the legacy system behaves and 1,002 physicians have more than one
  // historical number, so a live lookup would silently rewrite history.
  function choose(entry: DirectoryEntry) {
    onChange({ recipient_id: entry.id, fax: entry.fax_display })
    setOpen(false)
    setQuery('')
  }

  return (
    <fieldset className="recipient">
      <legend>{recipient.label}</legend>

      <div className="field">
        <label>Recipient</label>
        <div className="picker">
          <input
            type="text"
            value={open ? query : recipient.recipient_name ?? ''}
            placeholder={v.recipient_id ? '' : 'Search the directory…'}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onChange={(e) => setQuery(e.target.value)}
            /* The query is matched against surnames. iOS autocorrect turning
               one into a dictionary word returns no rows, and "no recipients
               match" is indistinguishable from an empty directory. */
            autoCapitalize="words"
            autoCorrect="off"
            spellCheck={false}
          />
          {v.recipient_id != null && !open && (
            <button
              type="button"
              className="link"
              disabled={disabled}
              onClick={() => onChange({ recipient_id: null, fax: '' })}
            >
              clear
            </button>
          )}
          {open && (
            <ul className="results">
              {results.map((e) => (
                <li key={e.id}>
                  <button type="button" onClick={() => choose(e)}>
                    <span>{e.name}</span>
                    <span className="subtle">{e.fax_display || 'no fax on file'}</span>
                  </button>
                </li>
              ))}
              {results.length === 0 && <li className="subtle">No matches</li>}
            </ul>
          )}
        </div>
      </div>

      <div className="field">
        <label htmlFor={`${recipient.slot}-fax`}>Fax number</label>
        <input
          id={`${recipient.slot}-fax`}
          type="tel"
          inputMode="tel"
          value={v.fax ?? ''}
          placeholder="(225) 555-0143"
          disabled={disabled}
          onChange={(e) => onChange({ fax: e.target.value })}
        />
        {recipient.fax_is_placeholder && (
          <p className="hint hint-warn">
            This looks like a placeholder, not a real number. Clear it if the fax
            is unknown.
          </p>
        )}
        {recipient.drifted && (
          <p className="hint">
            Directory has <strong>{recipient.directory_fax_display}</strong>
            <button
              type="button"
              className="link"
              disabled={disabled}
              onClick={() => onChange({ fax: recipient.directory_fax_display })}
            >
              use it
            </button>
          </p>
        )}
      </div>

      <div className="field field-check">
        <label>
          <input
            type="checkbox"
            checked={v.include}
            disabled={disabled}
            onChange={(e) => onChange({ include: e.target.checked })}
          />
          Include in fax distribution
        </label>
        {recipient.invariant_warning && (
          <p className="hint hint-warn">{recipient.invariant_warning}</p>
        )}
      </div>
    </fieldset>
  )
}
